// src/pages/Analytics.js
import React from 'react';
import FeaturePageLayout from '../components/FeaturePageLayout';

// Import Icons and Images
import { 
    IoAnalyticsOutline, 
    IoBarChartOutline, 
    IoBulbOutline, 
    IoPeopleCircleOutline, 
    IoTrendingUpOutline 
} from 'react-icons/io5';
import image1 from '../assets/images/Analytics.png';
import image2 from '../assets/images/EngagementRate.png';
import image3 from '../assets/images/Competitor.png';
import image4 from '../assets/images/Analytics2.png';
// ✅ Import the new, unique background image for this page
import heroBg from '../assets/images/analytics-background.png'; 

// --- Structured Data Schemas ---
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{
    "@type": "ListItem",
    "position": 1,
    "name": "Home",
    "item": "https://oolook.in/"
  },{
    "@type": "ListItem",
    "position": 2,
    "name": "Analytics",
    "item": "https://oolook.in/analytics"
  }]
};

const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Social Media Analytics Platform",
    "provider": {
        "@type": "Organization",
        "name": "Oolook"
    },
    "name": "AI-Powered Social Media Analytics & Reporting",
    "description": "Oolook's AI analytics turns raw social media data into actionable insights, helping you prove ROI, understand your audience, and optimize your strategy.",
    "url": "https://oolook.in/analytics"
};

// --- Page-Specific Data ---
const analyticsPageData = {
  seo: {
    title: "AI-Powered Social Media Analytics | Track & Optimize Performance | Oolook",
    description: "Go beyond likes and followers. Oolook's AI analytics turns raw data into actionable insights, helping you prove ROI, understand your audience, and optimize your social media strategy.",
    image: 'https://oolook.in/og/analytics.png',
    schemas: [breadcrumbSchema, serviceSchema]
  },
  hero: {
    icon: IoAnalyticsOutline,
    title: "Go Beyond Likes. Uncover What Works.",
    subtitle: "Transform raw social data into clear, actionable insights. Oolook's AI-powered analytics reveal the 'why' behind your performance, so you can prove ROI and grow faster.",
    // ✅ Use the new background image here
    backgroundImage: heroBg 
  },
  featureSection: {
    title: "Your Intelligent Analytics Hub",
    subtitle: "From high-level trends to post-by-post performance, get the complete picture without drowning in spreadsheets.",
    features: [
      { title: "Unified Performance Dashboard", description: "Monitor all your key metrics across every social platform from one clean, centralized dashboard.", image: image1 },
      { title: "Deep Audience Insights", description: "Understand your audience's demographics, interests, and online behavior to create content that truly connects.", image: image2 },
      { title: "Competitor Benchmarking", description: "See how your strategy stacks up against the competition and identify opportunities to outperform them.", image: image3 },
      { title: "Customizable, Beautiful Reports", description: "Generate and schedule stunning, easy-to-understand reports to share with your team or clients.", image: image4 }
    ]
  },
  benefitGrid: {
    title: "The Advantage of Data-Driven Decisions",
    benefits: [
      { icon: IoBarChartOutline, title: "Prove Your ROI", description: "Connect social media efforts to tangible business outcomes with clear conversion and revenue tracking." },
      { icon: IoBulbOutline, title: "Optimize Content Strategy", description: "Stop guessing. Identify your top-performing content and learn why it works, so you can create more of it." },
      { icon: IoPeopleCircleOutline, title: "Understand Your Audience", description: "Build a loyal community by understanding what your followers truly care about and respond to." },
      { icon: IoTrendingUpOutline, title: "Identify Trends Early", description: "Stay ahead of the curve by spotting emerging topics and hashtag trends before they take off." }
    ]
  },
  stepByStep: {
    title: "From Data to Decisions in Minutes",
    subtitle: "Our streamlined process makes sophisticated analysis simple and accessible for everyone on your team.",
    steps: [
      { title: "1. One-Click Integration", description: "Securely connect your social accounts, and Oolook immediately begins processing historical and real-time data." },
      { title: "2. Let AI Analyze", description: "Our intelligent engine analyzes millions of data points, identifying patterns, trends, and growth opportunities automatically." },
      { title: "3. Explore Your Dashboards", description: "Dive into beautiful, interactive dashboards that make complex data easy to understand and explore." },
      { title: "4. Schedule & Share Reports", description: "Automate reporting to your stakeholders. Keep everyone informed with scheduled email updates and shareable links." }
    ]
  },
  targetAudience: {
    title: "Actionable Insights for Every Role"
  }
};

const Analytics = () => {
  return (
    <FeaturePageLayout pageData={analyticsPageData} />
  );
};

export default Analytics;